package one.healthium.bland.presentation.navigation.home

import com.arkivanov.decompose.ComponentContext
import com.arkivanov.decompose.router.stack.ChildStack
import com.arkivanov.decompose.router.stack.StackNavigation
import com.arkivanov.decompose.router.stack.bringToFront
import com.arkivanov.decompose.router.stack.childStack
import com.arkivanov.decompose.router.stack.childStackWebNavigation
import com.arkivanov.decompose.router.webhistory.WebNavigation
import com.arkivanov.decompose.value.Value
import kotlinx.serialization.Serializable
import one.healthium.bland.presentation.navigation.today.DefaultTodayComponent
import one.healthium.content.navigation.insights.DefaultInsightsComponent
import one.healthium.insights.domain.models.ActionModel
import one.healthium.intercom.Intercom
import one.healthium.profile.navigation.DefaultProfileMainComponent
import one.healthium.profile.ui.food_history.models.FoodItemUiModel
import one.healthium.tasks.navigation.DefaultDailyTasksComponent
import one.primehealth.app.DeepLink
import kotlin.time.Instant

class DefaultHomeComponent(
    componentContext: ComponentContext,
    private val onInsightClicked: (insightId: String) -> Unit,
    private val onLogFoodClicked: (taskId: String?) -> Unit,
    private val onLogWaterClicked: (taskId: String?) -> Unit,
    private val onLogWeightClicked: (taskId: String?) -> Unit,
    private val onLogSymptomsClicked: (taskId: String?) -> Unit,
    private val onMealPlanClicked: (String) -> Unit,
    private val onWeightHistoryClicked: (Float, Instant) -> Unit,
    private val onWorkoutClicked: (String) -> Unit,
    private val onFoodHistoryItemClicked: (FoodItemUiModel) -> Unit,
    private val onActionClicked: (ActionModel) -> Unit,
    private val onUpdateNameClicked: () -> Unit,
    private val onUpdateAgeClicked: () -> Unit,
    private val onUpdateHeightClicked: () -> Unit,
    private val onUpdateInitialWeightClicked: () -> Unit,
    private val onUpdateTargetWeightClicked: () -> Unit,
) : HomeComponent, ComponentContext by componentContext {

    private val nav = StackNavigation<Config>()

    private val _stack = childStack(
        source = nav,
        serializer = Config.serializer(),
        initialStack = { listOf(Config.Today) },
        childFactory = ::child,
    )

    private fun child(config: Config, componentContext: ComponentContext): HomeComponent.Child {
        return when (config) {
            Config.Today -> HomeComponent.Child.TodayChild(
                DefaultTodayComponent(
                    componentContext = componentContext,
                    onMealPlanClicked = onMealPlanClicked,
                    onLogFoodClicked = { onLogFoodClicked(null) },
                    onLogWaterClicked = { onLogWaterClicked(null) },
                    onLogWeightClicked = { onLogWeightClicked(null) },
                    onLogSymptomsClicked = { onLogSymptomsClicked(null) },
                )
            )

            Config.DailyTasks -> HomeComponent.Child.DailyTasksChild(
                DefaultDailyTasksComponent(
                    componentContext = componentContext,
                    onLogWeightTaskClicked = onLogWeightClicked,
                    onLogWaterTaskClicked = onLogWaterClicked,
                    onLogFoodTaskClicked = onLogFoodClicked,
                    onReadInsightTaskClicked = onInsightClicked,
                    onDoWorkoutTaskClicked = onWorkoutClicked,
                    onLogSymptomsTaskClicked = onLogSymptomsClicked,
                )
            )

            Config.Insights -> HomeComponent.Child.InsightsChild(
                DefaultInsightsComponent(
                    componentContext = componentContext,
                    onActionClicked = onActionClicked,
                )
            )

            Config.Chat -> HomeComponent.Child.ChatChild
            Config.Profile -> HomeComponent.Child.ProfileChild(
                DefaultProfileMainComponent(
                    componentContext = componentContext,
                    onContactUsClicked = ::onChatTabClicked,
                    onWeightHistoryClicked = onWeightHistoryClicked,
                    onFoodHistoryItemClicked = onFoodHistoryItemClicked,
                    onUpdateNameClicked = onUpdateNameClicked,
                    onUpdateAgeClicked = onUpdateAgeClicked,
                    onUpdateHeightClicked = onUpdateHeightClicked,
                    onUpdateInitialWeightClicked = onUpdateInitialWeightClicked,
                    onUpdateTargetWeightClicked = onUpdateTargetWeightClicked,
                )
            )
        }
    }

    override val stack: Value<ChildStack<*, HomeComponent.Child>> = _stack

    override val webNavigation: WebNavigation<*> =
        childStackWebNavigation(
            navigator = nav,
            stack = _stack,
            enableHistory = false,
            serializer = Config.serializer(),
            childSelector = {
                when (val child = it.instance) {
                    is HomeComponent.Child.ProfileChild -> child.component
                    else -> null
                }
            }
        )

    override fun onTodayTabClicked() {
        nav.bringToFront(Config.Today)
    }

    override fun onDailyTasksTabClicked() {
        nav.bringToFront(Config.DailyTasks)
    }

    override fun onInsightsTabClicked() {
        nav.bringToFront(Config.Insights)
    }

    override suspend fun onChatTabClicked() {
        Intercom.present()
    }

    override fun onProfileTabClicked() {
        nav.bringToFront(Config.Profile)
    }

    override fun onDeepLink(deepLink: DeepLink) {
        when (deepLink) {
            DeepLink.Today -> onTodayTabClicked()
            DeepLink.DailyTasks -> onDailyTasksTabClicked()
            DeepLink.Insights -> onInsightsTabClicked()
            else -> Unit
        }
    }

    @Serializable
    private sealed interface Config {
        @Serializable
        data object Today : Config

        @Serializable
        data object DailyTasks : Config

        @Serializable
        data object Insights : Config

        @Serializable
        data object Chat : Config

        @Serializable
        data object Profile : Config
    }
}